/* ============================================================================
   ReportCardModal / styles.js
   Réplica do boletim impresso da escola (GradeIstquarter), adaptada para MODAL
   no portal do responsável. Mantém a identidade visual original:
     - cabeçalho azul (#007bff), bordas roxas (#a06cf0), logo da escola
     - tabela por matéria (nome à esquerda, bimestres à direita)
     - cores das notas/conceitos idênticas ao original
   Arquivo AUTOSSUFICIENTE (importa styled/keyframes; sem dependências externas).
   ========================================================================== */

   import styled, { keyframes, createGlobalStyle } from 'styled-components'

   const fadeIn = keyframes`
     from { opacity: 0; transform: translateY(10px); }
     to   { opacity: 1; transform: translateY(0); }
   `

   /* ---------- impressão isolada do boletim -----------------------------------
      Quando o usuário clica em "Imprimir", o componente adiciona a classe
      `printing-report-card` no <body>. Estas regras então:
        1. escondem TODA a página (dashboard, hero, gráficos, cards…)
        2. reexibem apenas a árvore do modal do boletim (#report-card-print-root)
        3. reposicionam o boletim no topo, ocupando a folha inteira
      Assim a impressão fica idêntica à da gestão da escola: só o boletim.
      ------------------------------------------------------------------------- */
   export const PrintIsolation = createGlobalStyle`
     @media print {
       body.printing-report-card {
         margin: 0 !important;
         padding: 0 !important;
         background: var(--surface) !important;
       }

       /* esconde tudo do app durante a impressão do boletim */
       body.printing-report-card * {
         visibility: hidden !important;
       }

       /* reexibe apenas o modal do boletim e seus descendentes */
       body.printing-report-card #report-card-print-root,
       body.printing-report-card #report-card-print-root * {
         visibility: visible !important;
       }

       /* tira o overlay do fluxo fixo e cola o boletim no topo da folha */
       body.printing-report-card #report-card-print-root {
         position: absolute !important;
         inset: 0 !important;
         left: 0 !important;
         top: 0 !important;
         width: 100% !important;
         height: auto !important;
         padding: 0 !important;
         margin: 0 !important;
         background: var(--surface) !important;
         display: block !important;
         overflow: visible !important;
       }

       /* força a impressão de TODAS as cores de fundo do boletim
          (quadradinhos da legenda, faixa azul do período, célula final…);
          navegadores omitem backgrounds por padrão ao imprimir. */
       body.printing-report-card #report-card-print-root,
       body.printing-report-card #report-card-print-root * {
         -webkit-print-color-adjust: exact !important;
         print-color-adjust: exact !important;
       }

       /* evita quebra de matérias no meio ao paginar */
       body.printing-report-card #report-card-print-root li {
         break-inside: avoid;
         page-break-inside: avoid;
       }
     }
   `
   
   /* ---------- overlay + caixa do modal --------------------------------------- */
   export const Overlay = styled.div`
     position: fixed;
     inset: 0;
     z-index: 1000;
     display: flex;
     align-items: flex-start;
     justify-content: center;
     padding: 24px 16px;
     overflow-y: auto;
     background: rgba(20, 10, 40, 0.5);
   
     @media (max-width: 768px) { padding: 10px; }
   
     @media print {
       position: static;
       background: none;
       padding: 0;
       overflow: visible;
     }
   `
   
   export const ModalCard = styled.div`
     width: 100%;
     max-width: 900px;
     background: var(--surface);
     border-radius: 12px;
     box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
     overflow: hidden;
     animation: ${fadeIn} 0.25s ease both;
   
     @media print {
       max-width: 100%;
       box-shadow: none;
       border-radius: 0;
       animation: none;
     }
   `
   
   /* ---------- barra de topo (fechar / imprimir) — some na impressão ---------- */
   export const TopBar = styled.div`
     display: flex;
     align-items: center;
     justify-content: space-between;
     gap: 10px;
     padding: 12px 16px;
     border-bottom: 1px solid var(--line);
   
     @media print { display: none; }
   `
   
   export const CloseButton = styled.button`
     display: inline-flex;
     align-items: center;
     justify-content: center;
     width: 34px;
     height: 34px;
     border: 1px solid var(--line);
     border-radius: 8px;
     background: var(--surface);
     color: var(--text, var(--text-muted));
     cursor: pointer;
     transition: background 0.15s;
   
     &:hover { background: var(--surface); }
   `
   
   export const PrintButton = styled.button`
     display: inline-flex;
     align-items: center;
     gap: 6px;
     color: #fff;
     background-color: #4caf50;
     border: none;
     border-radius: 6px;
     padding: 9px 18px;
     font-size: 14px;
     font-weight: 600;
     cursor: pointer;
     transition: background 0.15s;
   
     &:hover { background-color: #45a049; }
   `
   
   /* ---------- navegação de ano ----------------------------------------------- */
   export const YearNav = styled.div`
     display: flex;
     align-items: center;
     justify-content: center;
     gap: 14px;
     padding: 12px 16px 0;
   
     @media print { display: none; }
   `
   
   export const YearButton = styled.button`
     display: inline-flex;
     align-items: center;
     justify-content: center;
     width: 32px;
     height: 32px;
     border: 1px solid var(--line);
     border-radius: 8px;
     background: var(--surface);
     color: var(--text, var(--text-muted));
     cursor: pointer;
   
     &:hover:not(:disabled) { background: #f0f0f0; }
     &:disabled { opacity: 0.4; cursor: not-allowed; }
   `
   
   export const YearLabel = styled.span`
     min-width: 90px;
     text-align: center;
     font-size: 18px;
     font-weight: 700;
     color: var(--text, var(--text-muted));
   `
   
   /* ---------- corpo do boletim (área impressa) ------------------------------- */
   export const Body = styled.div`
     padding: 16px 24px 24px;
     text-align: center;
   
     @media (max-width: 768px) { padding: 12px; }
   `
   
   export const Loading = styled.div`
     padding: 48px 0;
     text-align: center;
     color: var(--text, var(--text-muted));
   `
   
   export const Empty = styled.div`
     padding: 44px 16px;
     text-align: center;
     color: var(--text, var(--text-muted));
     font-size: 15px;
   `
   
   /* ---------- logo + título -------------------------------------------------- */
   export const ContLogo = styled.div`
     position: relative;
     display: flex;
     justify-content: center;
     align-items: center;
     width: 100%;
     padding-bottom: 20px;
   
     h2 { margin: 0; font-size: 2em; color: var(--text, var(--text-muted)); }
   
     @media (max-width: 600px) {
       padding-bottom: 10px;
       h2 { font-size: 1.4em; }
     }
   `
   
   export const Preview = styled.img`
     position: absolute;
     left: 0;
     width: 110px;
     height: 110px;
     object-fit: contain;
   
     @media (max-width: 600px) {
       width: 64px;
       height: 64px;
     }
   `
   
   /* ---------- faixa do período (azul) ---------------------------------------- */
   export const PeriodBar = styled.div`
     background-color: #007bff;
     color: #fff;
     padding: 6px;
     border-radius: 8px;
   
     h3 { margin: 0; font-size: 1.4em; }
   `
   
   /* ---------- dados do aluno + frequência ------------------------------------ */
   export const DadosStdt = styled.div`
     display: flex;
     flex-direction: column;
     align-items: flex-start;
     gap: 8px;
     padding: 12px 6px;
     width: 100%;
   
     span {
       @media (max-width: 375px) { font-size: 9pt; }
       @media (min-width: 375px) and (max-width: 768px) { font-size: 10pt; }
     }
   `
   
   export const UpContainer = styled.div`
     display: flex;
     justify-content: space-between;
     align-items: center;
     width: 100%;
     flex-wrap: wrap;
     gap: 10px;
   `
   
   export const InfoContainer = styled.div`
     display: flex;
     flex-direction: column;
     align-items: flex-start;
     text-align: left;
     gap: 3px;
   `
   
   export const SpanFrequency = styled.div`
     display: flex;
     flex-direction: column;
     align-items: flex-start;
     gap: 4px;
     padding-right: 20px;
   
     span { display: inline-flex; align-items: center; gap: 4px; }
   
     @media (max-width: 768px) { padding-right: 0; }
   `
   
   /* ---------- legenda -------------------------------------------------------- */
   export const LegendBox = styled.div`
     display: flex;
     min-width: 320px;
     background-color: var(--surface);
     border-radius: 5px;
     box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
     padding: 8px 12px;
     margin-top: 6px;
   
     h3 { text-align: center; margin: 0 0 4px; font-size: 1em; }
     p  { margin: 2px 0; font-size: 0.92em; text-align: left; }
   
     @media (max-width: 768px) { min-width: auto; width: 100%; display: grid; }
   `
   
   /* ---------- lista de matérias ---------------------------------------------- */
   export const DivDados = styled.div`
     background-color: #fafafa;
     width: 100%;
     padding: 5px;
     margin-top: 10px;
     border-radius: 8px;
     box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
     border-top: 1px solid #a06cf0;
     border-bottom: 1px solid #a06cf0;
   `
   
   export const List = styled.ul`
     display: flex;
     flex-direction: column;
     list-style: none;
     padding: 0;
     margin: 0;
   `
   
   export const Emp = styled.li`
     display: flex;
     align-items: center;
     justify-content: space-around;
     padding: 10px;
     border-bottom: 1px solid #a06cf0;
     font-size: 1.1em;
   
     &:last-child { border-bottom: none; }
   
     @media (max-width: 768px) {
       width: 100%;
       padding: 10px 4px;
       flex-direction: column;
       gap: 8px;
       font-size: 1em;
     }
   `
   
   export const DivNameMatter = styled.div`
     display: flex;
     align-items: center;
     justify-content: center;
     width: 50%;
   
     @media (max-width: 768px) {
       width: 100%;
       justify-content: flex-start;
       padding-left: 4px;
     }
   `
   
   export const SpanNameMatter = styled.span`
     font-weight: bold;
     color: var(--text, var(--text-muted));

     @media (max-width: 768px) { font-size: 0.95em; }
   `
   
   export const Grade = styled.div`
     display: flex;
     align-items: center;
     justify-content: center;
   
     @media (max-width: 768px) {
       width: 100%;
       justify-content: flex-start;
       /* deixa a tabela rolar horizontal se não couber, sem quebrar layout */
       overflow-x: auto;
       -webkit-overflow-scrolling: touch;
     }
   `
   
   /* ---------- tabela de bimestres (cada bim = header + cell empilhados) ------ */
   export const DivBimTable = styled.div`
     display: flex;
     justify-content: center;
   
     @media (max-width: 768px) {
       justify-content: flex-start;
       min-width: min-content;
     }
   `
   
   export const DivBimRow = styled.div`
     display: grid;
   `
   
   export const DivBimHeader = styled.div`
     text-align: center;
     font-weight: bold;
     padding: 3px 6px;
     border: 1px solid var(--line);
     background-color: var(--surface);
     font-size: 0.9em;
     white-space: nowrap;

     @media (max-width: 768px) { padding: 4px 8px; font-size: 0.82em; }
   `
   
   // cor da célula fiel ao boletim original:
   //   CONCEITO → A verde · B azul · C laranja · D vermelho
   //   NOTA     → ≥90% da total: verde · < média: vermelho · caso contrário: azul
   export const DivBimCell = styled.div`
     text-align: center;
     padding: 4px 6px;
     border: 1px solid var(--line);
     font-weight: bold;
     ${({ $format, $grade, $average, $total, $history }) => {
       if ($history && $format !== 'concept') return 'color: #7A6F9B;'
       if ($format === 'concept') {
         const L = String($grade || '').trim().toUpperCase()
         const color =
           L === 'A' ? 'var(--green)' :
           L === 'B' ? 'blue' :
           L === 'C' ? 'orange' :
           L === 'D' ? 'red' : 'black'
         return `color: ${color};`
       }
       const g = parseFloat(String($grade).replace(',', '.'))
       const avg = parseFloat(String($average).replace(',', '.'))
       const tot = parseFloat(String($total).replace(',', '.'))
       if (!isNaN(g) && !isNaN(avg) && !isNaN(tot)) {
         /* mesmas cores dos boletins e diários da escola */
         if (g >= 0.9 * tot) return 'color: #16a34a;'
         if (g < avg) return 'color: #dc2626;'
         return 'color: #2563eb;'
       }
       return 'color: var(--text, var(--text-muted));'
     }}
   
     &.final {
       background-color: #f4f0fa;
       border-color: #a06cf0;
     }
   `
   
   /* ---------- cabeçalho do boletim de NOTAS (3 colunas) ---------------------- */
   /* esquerda: por bimestre · meio: anual · direita: legenda de cores.
      No mobile empilha em coluna para caber em telas pequenas. */
   export const NotesHeader = styled.div`
     display: flex;
     align-items: stretch;
     justify-content: space-between;
     gap: 12px;
     width: 100%;
     margin-top: 6px;
     flex-wrap: wrap;

     @media (max-width: 768px) {
       flex-direction: column;
       gap: 10px;
     }

     /* impressão: a folha A4 cai na regra de "mobile" — força "por período" e "anual"
        lado a lado na mesma linha; a legenda vai inteira na linha de baixo */
     @media print {
       flex-direction: row !important;
       flex-wrap: wrap !important;
       gap: 6px !important;
       margin-top: 4px;
     }
   `

   export const ScaleCol = styled.div`
     display: flex;
     flex-direction: column;
     gap: 4px;
     padding: 8px 12px;
     border: 1px solid var(--line);
     border-radius: 8px;
     background: var(--surface);
     font-size: 0.95em;
     min-width: 0;
     flex: 1 1 auto;

     @media (max-width: 768px) {
       width: 100%;
       padding: 8px 10px;
       font-size: 0.9em;
     }

     @media print {
       width: auto !important;
       flex: 1 1 0 !important;
       padding: 4px 8px !important;
       font-size: 9pt !important;
       gap: 2px !important;
     }
   `

   export const ScaleColTitle = styled.div`
     font-size: 0.78em;
     font-weight: 700;
     text-transform: uppercase;
     letter-spacing: 0.4px;
     color: #a06cf0;
     border-bottom: 1px solid var(--line);
     padding-bottom: 3px;
     margin-bottom: 2px;
   `

   /* total/média de cada bimestre — sempre empilhados */
   export const ScalePeriods = styled.div`
     display: flex;
     flex-direction: column;
     gap: 3px;
     font-size: 0.9em;

     span { white-space: nowrap; }
     strong { color: var(--text, var(--text-muted)); }

     @media (max-width: 768px) { font-size: 0.95em; }
   `

   /* total/média anual — empilhados */
   export const ScaleAnnual = styled.div`
     display: flex;
     flex-direction: column;
     gap: 3px;

     span { white-space: nowrap; }
   `
   
   export const SpanTotalGrade = styled.span`
     color: #28a745;
     font-weight: bold;
   `
   
   export const SpanAverageGrade = styled.span`
     color: #007bff;
     font-weight: bold;
   `
   
   /* ---------- legenda de cores (só formato nota) ----------------------------- */
   export const LegendColors = styled.div`
     display: flex;
     flex-direction: column;
     gap: 6px;
     margin-top: 4px;
     align-items: flex-start;
     padding: 8px 12px;
     border: 1px solid var(--line);
     border-radius: 8px;
     background: var(--surface);
     flex: 1 1 auto;
   
     div { display: flex; align-items: center; gap: 6px; font-size: 13px; text-align: left; }
   
     .red-box, .blue-box, .green-box, .history-box {
       width: 14px; height: 14px; border-radius: 3px; display: inline-block; flex-shrink: 0;
       /* força a impressão da cor de fundo (navegadores omitem por padrão) */
       -webkit-print-color-adjust: exact;
       print-color-adjust: exact;
     }
     .red-box { background-color: #dc2626; }
     .blue-box { background-color: #2563eb; }
     .green-box { background-color: #16a34a; }
     .history-box { background-color: #7A6F9B; }

     @media (max-width: 768px) {
       width: 100%;
       div { font-size: 12.5px; }
     }

     @media print {
       flex: 1 1 100% !important;
       width: 100% !important;
       padding: 2px 6px !important;
       margin-top: 0 !important;
     }
   `
   
   /* ---------- assinaturas (só impressão) ------------------------------------- */
   export const DivSignatureArea = styled.div`
     display: none;
   
     @media print {
       display: flex;
       justify-content: space-around;
       margin-top: 60px;
       padding: 20px;
     }
   `
   
   export const SignatureBlock = styled.div`
     display: flex;
     flex-direction: column;
     align-items: center;
   `
   
   export const Line = styled.div`
     width: 200px;
     border-bottom: 1px solid #000;
     margin-bottom: 8px;
   `